import * as Yup from 'yup'

import { Disease } from '../../../ana-toolbox'
import { DiseaseType } from '../../../ana-toolbox'
import { medications } from '../config/medicaments'
import { createFields } from '../helpers/createFields'
import { buildValidationSchema } from '../yup/buildValidationSchema'
import { latinDiseasesSchema, latinTextListSchema } from '../yup/yup'

const fields = createFields({
  hasAllergies: {
    __type: 'radioGroup',
    name: 'hasAllergies',
    label: 'field.has_allergies',
    isRequired: true,
    helperText: 'field.has_allergies_helper',
    options: [
      {
        label: 'allergies_yes',
        value: 'YES',
      },
      {
        label: 'allergies_no',
        value: 'NO',
      },
      {
        label: 'allergies_maybe',
        value: 'UNKNOWN',
      },
    ],
    hooks: {
      beforeChange: (value, helpers, _event) => {
        if (value !== 'YES') {
          const { value: diseases } = helpers.getFieldProps<Disease[]>('diseases')

          const diseasesWithoutAllergies = diseases?.filter(
            (disease) =>
              disease.type !== DiseaseType.OTHER_ALLERGIES && disease.type !== DiseaseType.MEDICATION_ALLERGIES
          )

          helpers.setFieldValue('diseases', diseasesWithoutAllergies || [])
        }
      },
    },
  },
  medications: {
    __type: 'custom',
    name: 'medications',
    label: 'field.medications',
    helperText: 'field.medications_helper',
    options: medications.map((medication) => ({ label: `option.${medication}`, value: medication })),
    validation: latinTextListSchema,
  },
  allergyPass: {
    __type: 'toggle',
    name: 'allergyPass',
    label: 'field.allergy_pass',
    helperText: 'field.allergy_pass_helper',
    visibleWhen: ({ hasAllergies }) => hasAllergies === 'YES',
  },
  cigaretteConsumption: {
    __type: 'radioGroup',
    isRequired: true,
    label: 'field.is_smoker',
    name: 'cigaretteConsumption',
    validation: Yup.string().required(),
    inputProps: {
      withText: true,
    },
    options: [
      {
        label: 'ana.cigarette_consumption.daily',
        value: 'DAILY',
      },
      {
        label: 'ana.cigarette_consumption.daily_less_than_ten',
        value: 'DAILY_LESS_THAN_TEN',
      },
      {
        label: 'ana.cigarette_consumption.occasionally',
        value: 'OCCASIONALLY',
      },
      {
        label: 'ana.cigarette_consumption.stopped',
        value: 'STOPPED',
      },
      {
        label: 'ana.cigarette_consumption.never',
        value: 'NEVER',
      },
    ],
  },
  alcoholConsumption: {
    __type: 'radioGroup',
    isRequired: true,
    label: 'field.alcohol_consumption',
    name: 'alcoholConsumption',
    inputProps: {
      withText: true,
    },
    validation: Yup.string().required(),
    options: [
      {
        label: 'ana.alcohol_consumption.daily',
        value: 'DAILY',
      },
      {
        label: 'ana.alcohol_consumption.reguraly',
        value: 'SEVERAL_TIMES_A_WEEK',
      },
      {
        label: 'ana.alcohol_consumption.once_a_week',
        value: 'ONCE_A_WEEK',
      },
      {
        label: 'ana.alcohol_consumption.once_a_month',
        value: 'ONCE_A_MONTH',
      },
      {
        label: 'ana.alcohol_consumption.never',
        value: 'NEVER',
      },
    ],
  },
  drugConsumption: {
    __type: 'radioGroup',
    isRequired: true,
    label: 'field.drug_consumption',
    name: 'drugConsumption',
    validation: Yup.string().required(),
    options: [
      {
        label: 'ana.drug_consumption.regularly',
        value: 'CURRENTLY_REGULARLY',
      },
      {
        label: 'ana.drug_consumption.occasionally',
        value: 'CURRENTLY_OCCASIONALLY',
      },
      {
        label: 'ana.drug_consumption.regularly_in_past',
        value: 'REGULARLY_IN_PAST',
      },
      {
        label: 'ana.drug_consumption.occasionally_in_past',
        value: 'OCCASIONALLY_IN_PAST',
      },
      {
        label: 'ana.drug_consumption.never',
        value: 'NEVER',
      },
      {
        label: 'ana.drug_consumption.no_answer',
        value: 'PREFERRED_TO_NOT_ANSWER',
      },
    ],
    inputProps: {
      withText: true,
    },
  },
})

// The allergies are not declared as a field, they are stored in the `diseases` of the form
export const validationSchema = buildValidationSchema(fields).shape({ diseases: latinDiseasesSchema })

export default fields
